import PropTypes from 'prop-types';
import { Locale } from '@fixter/i18n';
import { queryCmsContent } from '$server/modules/cms';
import nextFetchRequest from '$api/nextFetchRequest';
import deferHydration from '$util/deferHydration';
import { getServerLogger } from '$util/index';
import { useAppContext } from '$util/AppContext';
import MainLayout from '$layout/MainLayout';
import { CarsTopSection } from '$components/pages/cars/CarsTopSection';
import { QUERY_GET_FOOTER_CMS_CONTENT } from '$components/layout/Footer/gql/queries/getFooterCmsContent';
import { QUERY_READ_FOOTER_DATA } from '$components/layout/Footer/gql/queries/readFooterData';
import { getReassuranceQuery } from '$components/composite/Reassurance/gql';
import { getSearchBoxQuery } from '$components/composite/SearchSuggestions/gql';
import { getSocialTrustQuery } from '$components/SocialTrustSection/gql';
import { getNavbarQuery } from '$commons/gql';
import { getVrmLookupQuery } from '$quantum/molecules/VrmLookup/gql';
import { getTeamSupportSection } from '$quantum/organisms/TeamSupportSection/gql';
import { getPromotionBannerQuery } from '$quantum/molecules/PromotionBanner/gql';
import { GET_CUSTOMER_SERVICE_CMS_QUERY } from '$cms/gql';
import { GET_HOMEPAGE_OPEN_GRAPH_CONTENT, GET_DOMAIN_VERIFICATION_CONTENT } from '$layout/MainLayout/gql';
import { CarMakesList } from '$src-components/molecules/CarMakesList';
import { QUERY_CAR_MAKES_LIST_CONTENT_COMPONENT } from '$src-components/molecules/CarMakesList/gql';
import { getLongTextByKey, getShortTextByKey, getLinkByKey } from '$cms/getDataByKey';
import { isPreviewActive } from '$server/modules/cms/util';
import { getLocaleFromHostname } from '$util/domains';
import { WithResponsiveGlobalStyles } from '$util/theme/withResponsive';
import { CACHE_CONTROL_CACHE_ENABLED, CACHE_CONTROL_NO_CACHE } from '../internals/next/config/headers';

const PAGE_NAME = 'index_cars';

const TeamSupportSection = deferHydration(() => import('$quantum/organisms/TeamSupportSection'), {
  hydrationId: '$components/TeamSupportSection',
  trigger: 'view',
});

const MechanicSection1 = deferHydration(() => import('$components/MechanicSection1'), {
  hydrationId: '$components/MechanicSection1',
  trigger: 'view',
});

const TryFixterSection = deferHydration(() => import('$components/TryFixterSection'), {
  hydrationId: '$components/TryFixterSection',
  trigger: 'view',
});

const TagsSection = deferHydration(() => import('$components/TagsSection'), {
  hydrationId: '$components/TagsSection',
  trigger: 'view',
});

const BreadcrumbsSection = deferHydration(() => import('$components/BreadcrumbsSection'), {
  hydrationId: '$components/BreadcrumbsSection',
  trigger: 'view',
});

const BookingStepsSection = deferHydration(() => import('$components/BookingStepsSection'), {
  hydrationId: '$components/BookingStepsSection',
  trigger: 'view',
});

const ReassuranceBlueIconsTheme = deferHydration(() => import('$components/ReassuranceBlueIconsTheme'), {
  hydrationId: '$components/ReassuranceBlueIconsTheme',
  trigger: 'view',
});

const SocialTrustSection = deferHydration(() => import('$components/SocialTrustSection'), {
  hydrationId: '$components/SocialTrustSection',
  trigger: 'view',
});

/**
 * @typedef {import("../src/components/molecules/CarMakesList").CarMakesListTranslatedEntities} CarMakesListTranslatedEntities
 */

const getCmsData = (cmsData) => {
  const getShortText = getShortTextByKey.bind(cmsData);
  const getLongText = getLongTextByKey.bind(cmsData);
  const getLink = getLinkByKey.bind(cmsData);

  /**
   * @type CarMakesListTranslatedEntities
   */
  const carMakesListTranslatedEntities = CarMakesList.schema.translatedEntities.parse({
    title: {
      key: `${CarMakesList.displayName}.title.reskin`,
      value: getShortText(`${CarMakesList.displayName}.title.reskin`, 'We cover all makes and models'),
    },
    viewAllLink: getLink(`${CarMakesList.displayName}.viewAllLink.reskin`, {
      text: 'View all covered car makes and models',
      url: '/cars',
    }),
  });

  return {
    metaTitle: getShortText('homepage.metadata.title', 'Car service, MOT and repair the easy way | Fixter'),
    metaDescription: getLongText(
      'homepage.metadata.description',
      `Looking for hassle-free MOT, car service, repair or maintenance? ` +
        `Let Fixter take the strain! Our local experts will collect and deliver ` +
        `your car to your door and make sure you get the right job done at a fair price.`
    ),
    carMakesListTranslatedEntities,
  };
};

function CarMakesPage({ tagsList }) {
  const {
    cmsContent: { dictionary },
  } = useAppContext();

  const { carMakesListTranslatedEntities } = getCmsData(dictionary);

  return (
    <MainLayout
      seo={{
        noindex: false,
        nofollow: false,
        title: 'Car service, MOT and repair the easy way | Fixter',
        metaDescription:
          `Looking for hassle-free MOT, car service, repair or maintenance? ` +
          `Let Fixter take the strain! Our local experts will collect and deliver ` +
          `your car to your door and make sure you get the right job done at a fair price.`,
      }}
    >
      <WithResponsiveGlobalStyles />
      <CarsTopSection />
      <SocialTrustSection />
      <ReassuranceBlueIconsTheme />
      <CarMakesList
        translatedEntities={carMakesListTranslatedEntities}
        configuration={{ showAllMakes: true }}
      />
      <MechanicSection1 />
      <TagsSection tagsList={tagsList} />
      <BookingStepsSection />
      <TryFixterSection />
      <TeamSupportSection source={PAGE_NAME} />
      <BreadcrumbsSection />
    </MainLayout>
  );
}

export const getServerSideProps = async function ({ req, res, query: urlQuery, preview: isPreviewMode }) {
  const log = getServerLogger(PAGE_NAME);
  const locale = getLocaleFromHostname(req.headers.host || '');

  if (locale !== Locale.EN_GB) {
    return {
      notFound: true,
    };
  }

  const serverParams = {
    pathname: 'cars',
    pageType: [],
    pageTypeFromPath: 'carMakes',
  };
  const { pathname, pageType, pageTypeFromPath } = serverParams;
  log.debug({ pathname, pageType, pageTypeFromPath }, 'getServerSideProps for page %s', PAGE_NAME);

  const nextFetch = nextFetchRequest(req);

  const preview = isPreviewActive(urlQuery, isPreviewMode);
  const cmsContent = await queryCmsContent(PAGE_NAME, {
    cmsQuery: [
      GET_HOMEPAGE_OPEN_GRAPH_CONTENT,
      GET_DOMAIN_VERIFICATION_CONTENT,
      getNavbarQuery,
      getVrmLookupQuery,
      QUERY_GET_FOOTER_CMS_CONTENT,
      getReassuranceQuery,
      getSearchBoxQuery,
      getSocialTrustQuery,
      GET_CUSTOMER_SERVICE_CMS_QUERY,
      getTeamSupportSection,
      getPromotionBannerQuery,
      QUERY_CAR_MAKES_LIST_CONTENT_COMPONENT,
    ],
    resQuery: [QUERY_READ_FOOTER_DATA],
    context: {
      variables: { locale },
      preview,
    },
  });

  // request data in parallel
  const tagsList = await nextFetch('/api/tags-list', {
    pathnameDataList: pageType,
    pathnameCombination: pageTypeFromPath,
  }).catch((error) => {
    // don't crash page if there's an error
    log.error({ error }, 'Failed to fetch tags-list');
    return {};
  });

  if (preview) res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
  else res.setHeader('Cache-Control', CACHE_CONTROL_CACHE_ENABLED);
  return { props: { serverParams, tagsList, cmsContent } };
};

CarMakesPage.propTypes = {
  tagsList: PropTypes.object.isRequired,
};

export default CarMakesPage;
